import { Link } from 'react-router';
import { MdShield, MdArrowForward } from 'react-icons/md';
import { getAuthUrl } from '@podus/utils';
import AuthLayout from '@/components/AuthLayout';
import { useAuth } from '@/context/AuthContext';
import { useNoIndex } from '@/lib/useNoIndex';

/**
 * Admin entry point.
 *
 * Nothing public links here — staff reach this URL directly — so the page is
 * also kept out of search results.
 *
 * This platform deliberately holds no admin credentials: programme admins are
 * existing Podus staff accounts, so sign-in is delegated to auth.podus.org and
 * we accept the token it hands back at `/admin/callback`.
 */
export default function AdminSignIn() {
    const { startAdminSignIn } = useAuth();
    useNoIndex();

    // Name the host the button actually sends them to, so a dev build doesn't
    // promise auth.podus.org and then navigate to localhost.
    const authHost = getAuthUrl().replace(/^https?:\/\//, '');

    return (
        <AuthLayout
            title="Admin sign in"
            subtitle="Programme admins sign in with their Podus staff account."
            footer={
                <>
                    Are you an ambassador?{' '}
                    <Link to="/sign-in" className="font-bold text-primary">
                        Sign in here
                    </Link>
                </>
            }
        >
            <div className="rounded-xl border border-[#E0E0E0] bg-[#fafafa] p-6">
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-primary/10 text-primary">
                    <MdShield size={22} />
                </span>
                <p className="mt-4 text-[14px] leading-6 text-[#747474]">
                    You&rsquo;ll be taken to <strong className="text-secondary">{authHost}</strong> to verify your
                    identity, then brought straight back here. Only Admin and Super Admin accounts can open the
                    programme dashboard.
                </p>

                <button
                    type="button"
                    onClick={startAdminSignIn}
                    className="mt-6 flex h-12 w-full items-center justify-center gap-2 rounded-[10px] bg-secondary text-[14px] font-bold text-white transition-opacity hover:opacity-90"
                >
                    Continue to Podus sign in
                    <MdArrowForward size={18} />
                </button>
            </div>
        </AuthLayout>
    );
}
